import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../lib/api.js";
import { chunkForTts, synthesizeTtsChunk, ttsChunkMax } from "../lib/tts.js";
import { ttsLog } from "../lib/ttsLog.js";

export type TtsState = "idle" | "loading" | "playing" | "paused";

export interface TtsProviderInfo {
  id: string;
  label: string;
  reachable: boolean;
}

export interface TtsStatusInfo {
  available: boolean;
  provider: string | null;
  providerLabel: string;
  voices: string[];
  defaultVoice: string;
  providers: TtsProviderInfo[];
}

/**
 * Read-aloud for the manuscript. Uses realistic server-side neural TTS when a
 * provider is configured (fetching sentence-bounded chunks and prefetching the
 * next while the current plays), and falls back to the browser's device voice
 * otherwise. Never blocks typing; everything tears down cleanly on stop/unmount.
 */
export function useTts(projectId: string) {
  const [status, setStatus] = useState<TtsStatusInfo | null>(null);
  const [state, setState] = useState<TtsState>("idle");
  const [progress, setProgress] = useState({ index: 0, total: 0 });
  /** 0-based index of the chunk whose audio is actually sounding now (for read-along). */
  const [nowPlaying, setNowPlaying] = useState(-1);
  /** The provider actually producing audio right now (updated from each chunk). */
  const [activeProviderLabel, setActiveProviderLabel] = useState<string | null>(null);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const chunksRef = useRef<string[]>([]);
  const engineRef = useRef<"openai" | "device" | null>(null);
  // Chunk index -> in-flight/resolved synthesis promise. Storing the PROMISE (not
  // just the URL) means a chunk already being prefetched is awaited, not fetched
  // a second time — which otherwise caused a long silent gap when the current
  // clip ended before its successor's prefetch finished.
  const prefetch = useRef<Map<number, Promise<string>>>(new Map());
  // A monotonically increasing id for the current playback run. Every teardown
  // bumps it, so a late callback from a previous run (e.g. a cancelled
  // utterance's onend firing after a new play started) is ignored instead of
  // spawning a second, interleaving playback chain — the "bouncing" bug.
  const runId = useRef(0);
  // Device-voice keep-alive: Chrome silences speechSynthesis after ~15s of
  // continuous speech; a periodic resume() (no pause, so it never restarts an
  // utterance) keeps a long read-through going. Cleared on teardown.
  const keepAlive = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    let active = true;
    api
      .ttsStatus(projectId)
      .then((s) => {
        if (!active) return;
        setStatus(s);
        // Show the preferred provider up front (device voice when none is reachable).
        setActiveProviderLabel(s.available ? s.providerLabel : "Device Voice");
      })
      .catch(() => active && setStatus(null));
    return () => { active = false; };
  }, [projectId]);

  const teardown = useCallback(() => {
    ttsLog("teardown", { runId: runId.current });
    runId.current++; // invalidate any in-flight chain/callbacks from the old run
    if (keepAlive.current) {
      clearInterval(keepAlive.current);
      keepAlive.current = null;
    }
    if (audioRef.current) {
      audioRef.current.onended = null;
      audioRef.current.pause();
      audioRef.current.removeAttribute("src");
    }
    if (typeof window !== "undefined" && window.speechSynthesis) window.speechSynthesis.cancel();
    for (const p of prefetch.current.values()) void p.then((u) => URL.revokeObjectURL(u)).catch(() => {});
    prefetch.current.clear();
  }, []);

  useEffect(() => () => teardown(), [teardown]);

  const stop = useCallback(() => {
    teardown();
    setState("idle");
    setProgress({ index: 0, total: 0 });
    setNowPlaying(-1);
  }, [teardown]);

  // ---- Server (neural) engine: Blueprint (local Chatterbox) / OpenAI ----
  const fetchChunk = useCallback(
    (i: number, voice: string): Promise<string> => {
      const existing = prefetch.current.get(i);
      if (existing) return existing; // already fetched or in flight — don't duplicate
      const promise = (async () => {
        // One retry so a transient per-chunk failure (e.g. Blueprint 502) doesn't
        // silently end the read-through on that chunk.
        let result;
        const started = Date.now();
        try {
          result = await synthesizeTtsChunk(projectId, chunksRef.current[i]!, voice);
        } catch (err) {
          ttsLog("chunk.fetch.retry", { i, error: err instanceof Error ? err.message : String(err) });
          result = await synthesizeTtsChunk(projectId, chunksRef.current[i]!, voice);
        }
        ttsLog("chunk.fetched", {
          i,
          chars: chunksRef.current[i]!.length,
          ms: Date.now() - started,
          provider: result.providerId,
          engine: result.engine
        });
        // Reflect the provider the server actually used (e.g. a visible fall-through
        // from Blueprint to OpenAI), never a silent switch. If Blueprint quietly
        // used Kokoro for a chunk, flag the wrong-voice fallback instead of hiding it.
        if (result.providerLabel) {
          const kokoro = result.engine && result.engine.toLowerCase().includes("kokoro");
          setActiveProviderLabel(kokoro ? `${result.providerLabel} ⚠ Kokoro fallback` : result.providerLabel);
        }
        return result.url;
      })();
      // If it ultimately fails, drop it so a later attempt can retry.
      promise.catch(() => {
        if (prefetch.current.get(i) === promise) prefetch.current.delete(i);
      });
      prefetch.current.set(i, promise);
      return promise;
    },
    [projectId]
  );

  const playFrom = useCallback(
    async (i: number, voice: string, rid: number): Promise<void> => {
      if (rid !== runId.current) return;
      if (i >= chunksRef.current.length) {
        stop();
        return;
      }
      setProgress({ index: i + 1, total: chunksRef.current.length });
      const url = await fetchChunk(i, voice);
      if (rid !== runId.current) return;
      const audio = audioRef.current!;
      audio.src = url;
      if (i + 1 < chunksRef.current.length) void fetchChunk(i + 1, voice).catch(() => {});
      audio.onloadedmetadata = () => ttsLog("audio.meta", { i, durationSec: Number(audio.duration.toFixed(1)) });
      // Advance exactly once whether the clip ends or errors.
      let advanced = false;
      const advance = (why: string) => {
        if (advanced || rid !== runId.current) return;
        advanced = true;
        ttsLog("audio.advance", { i, why, atSec: Number(audio.currentTime.toFixed(1)), durationSec: Number(audio.duration.toFixed(1)) });
        const prev = prefetch.current.get(i);
        if (prev) {
          void prev.then((u) => URL.revokeObjectURL(u)).catch(() => {});
          prefetch.current.delete(i);
        }
        void playFrom(i + 1, voice, rid);
      };
      audio.onended = () => advance("ended");
      audio.onerror = () => advance("error");
      ttsLog("audio.play", { i });
      await audio.play();
      if (rid === runId.current) {
        setState("playing");
        setNowPlaying(i); // audio is actually sounding now → sync read-along
      }
    },
    [fetchChunk, stop]
  );

  // ---- Device engine ----
  // One utterance at a time, chained strictly in order via onend. No pause/resume
  // hack (it makes Chrome restart utterances, which sounds like "bouncing"); the
  // small chunk size keeps each utterance under Chrome's ~15s cutoff instead.
  const playDeviceFrom = useCallback((i: number, rid: number) => {
    if (rid !== runId.current) return;
    if (i >= chunksRef.current.length) {
      stop();
      return;
    }
    setProgress({ index: i + 1, total: chunksRef.current.length });
    setActiveProviderLabel("Device Voice");
    ttsLog("device.speak", { i, chars: chunksRef.current[i]!.length });
    const utter = new SpeechSynthesisUtterance(chunksRef.current[i]!);
    // Advance exactly once: Chrome can fire both onend and onerror for the same
    // utterance; without this guard that double-advance would skip a chunk.
    let advanced = false;
    const advance = (why: string) => {
      if (advanced || rid !== runId.current) return;
      advanced = true;
      ttsLog("device.advance", { i, why });
      playDeviceFrom(i + 1, rid);
    };
    utter.onstart = () => { if (rid === runId.current) setNowPlaying(i); };
    utter.onend = () => advance("end");
    utter.onerror = (e) => advance(`error:${(e as SpeechSynthesisErrorEvent).error ?? "?"}`);
    setState("playing");
    window.speechSynthesis.speak(utter);
    if (!keepAlive.current) {
      keepAlive.current = setInterval(() => {
        try {
          window.speechSynthesis.resume();
        } catch {
          /* ignore */
        }
      }, 5000);
    }
  }, [stop]);

  const play = useCallback(
    async (text: string, voice?: string): Promise<void> => {
      teardown(); // bumps runId; this run owns the new value
      const rid = runId.current;
      const serverAvailable = !!status?.available;
      setNowPlaying(-1);
      // Both engines need small, sentence-sized chunks (see ttsChunkMax): the
      // neural engine returns a truncated clip past ~400 chars, and the browser
      // voice gets cut off by Chrome after ~15s. Split on sentence boundaries so
      // playback is complete and in order.
      const chunks = chunkForTts(text, ttsChunkMax(serverAvailable));
      ttsLog("play", {
        runId: rid,
        serverAvailable,
        provider: status?.provider ?? null,
        textChars: text.length,
        chunks: chunks.length,
        maxChunkChars: chunks.reduce((m, c) => Math.max(m, c.length), 0),
        build: "neural320"
      });
      if (chunks.length === 0) return;
      chunksRef.current = chunks;
      setProgress({ index: 0, total: chunks.length });
      setState("loading");

      if (serverAvailable) {
        engineRef.current = "openai";
        if (!audioRef.current) audioRef.current = new Audio();
        await playFrom(0, voice || status!.defaultVoice || "alloy", rid);
      } else if (typeof window !== "undefined" && "speechSynthesis" in window) {
        engineRef.current = "device";
        // Chrome keeps a stale queue across page lives; clear it before we start.
        window.speechSynthesis.cancel();
        playDeviceFrom(0, rid);
      } else {
        setState("idle");
        throw new Error("No narration voice is available.");
      }
    },
    [status, teardown, playFrom, playDeviceFrom]
  );

  const pause = useCallback(() => {
    if (engineRef.current === "openai") audioRef.current?.pause();
    else window.speechSynthesis?.pause();
    setState("paused");
  }, []);

  const resume = useCallback(() => {
    if (engineRef.current === "openai") void audioRef.current?.play();
    else window.speechSynthesis?.resume();
    setState("playing");
  }, []);

  return { status, state, progress, nowPlaying, providerLabel: activeProviderLabel, engine: engineRef.current, play, pause, resume, stop };
}
